import React from "react";

/**
 * SynapseLoader — spinner Synappsi on-brand
 * Due neuroni pulsanti collegati da un assone luminoso che pulsa in loop.
 * Uso: <SynapseLoader />  oppure  <SynapseLoader label="Sto caricando…" />
 * Props:
 *   size: "sm" | "md" | "lg" | "xl"  (default "md")
 *   label: stringa opzionale sotto lo spinner
 *   inline: true per usarlo dentro un bottone (nessun padding, nessuna label)
 *   className: classi extra sul wrapper
 */
const SIZES = {
    // Add smaller inline variant for buttons
    inline: { w: 32, strokeW: 2.4, dot: 3.5, label: "text-[10px]" },
    sm: { w: 44,  strokeW: 2.4, dot: 4,  label: "text-[10px]" },
  md: { w: 72,  strokeW: 2.8, dot: 6,  label: "text-xs" },
  lg: { w: 110, strokeW: 3.2, dot: 8,  label: "text-sm" },
  xl: { w: 160, strokeW: 3.6, dot: 10, label: "text-base" },
};

const KEYFRAMES = `
@keyframes syn-loader-axon {
  0%   { stroke-dashoffset: var(--syn-loader-len); opacity: 0.4; }
  45%  { stroke-dashoffset: 0;                       opacity: 1;   }
  55%  { stroke-dashoffset: 0;                       opacity: 1;   }
  100% { stroke-dashoffset: calc(var(--syn-loader-len) * -1); opacity: 0.4; }
}
@keyframes syn-loader-node-a {
  0%, 100% { transform: scale(0.85); filter: drop-shadow(0 0 4px currentColor); }
  30%      { transform: scale(1.25); filter: drop-shadow(0 0 12px currentColor); }
}
@keyframes syn-loader-node-b {
  0%, 100% { transform: scale(0.85); filter: drop-shadow(0 0 4px currentColor); }
  70%      { transform: scale(1.25); filter: drop-shadow(0 0 12px currentColor); }
}
@keyframes syn-loader-spark {
  0%   { offset-distance: 0%;   opacity: 0; }
  10%  { opacity: 1; }
  90%  { opacity: 1; }
  100% { offset-distance: 100%; opacity: 0; }
}
@keyframes syn-loader-label {
  0%, 100% { opacity: 0.55; }
  50%      { opacity: 1; }
}
`;

export default function SynapseLoader({ size = "md", label, inline = false, className = "" }) {
  const s = SIZES[size] || SIZES.md;
  const w = s.w;
  const h = Math.round(w * 0.55);
  // Percorso: arco S dolce da sinistra a destra
  const yTop = h * 0.28;
  const yBot = h * 0.72;
  const path = `M ${w * 0.16} ${yTop} C ${w * 0.42} ${yTop}, ${w * 0.58} ${yBot}, ${w * 0.84} ${yBot}`;
  // Lunghezza approssimativa per il dash (bezier)
  const dashLen = w * 0.9;
  const nodeAx = w * 0.16, nodeAy = yTop;
  const nodeBx = w * 0.84, nodeBy = yBot;

  return (
    <div className={`${inline ? "inline-flex" : "flex flex-col items-center justify-center gap-2"} ${className}`}
         role="status" aria-live="polite" data-testid="synapse-loader">
      <style>{KEYFRAMES}</style>
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true">
        <defs>
          <linearGradient id={`syn-loader-grad-${w}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%"   stopColor="#60A5FA" />
            <stop offset="100%" stopColor="#34D399" />
          </linearGradient>
          <radialGradient id={`syn-loader-halo-${w}`} cx="50%" cy="50%" r="50%">
            <stop offset="0%"   stopColor="#34D399" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#34D399" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Halo morbido sotto l'assone */}
        <ellipse cx={w / 2} cy={h / 2} rx={w * 0.42} ry={h * 0.35}
                 fill={`url(#syn-loader-halo-${w})`} opacity="0.6" />

        {/* Assone S-curve con pulsazione via dashoffset */}
        <path d={path}
              fill="none"
              stroke={`url(#syn-loader-grad-${w})`}
              strokeWidth={s.strokeW}
              strokeLinecap="round"
              style={{
                strokeDasharray: dashLen,
                "--syn-loader-len": dashLen,
                animation: "syn-loader-axon 1.6s ease-in-out infinite",
                filter: "drop-shadow(0 0 3px rgba(96,165,250,0.6))",
              }} />

        {/* Scintilla che scorre lungo l'assone (usa offset-path CSS) */}
        <circle r={s.dot * 0.55} fill="#FBBF24"
                style={{
                  offsetPath: `path("${path}")`,
                  offsetRotate: "auto",
                  animation: "syn-loader-spark 1.6s ease-in-out infinite",
                  filter: "drop-shadow(0 0 6px #FBBF24)",
                }} />

        {/* Neurone sinistro (blu) */}
        <g style={{
             transformOrigin: `${nodeAx}px ${nodeAy}px`,
             transformBox: "fill-box",
             color: "#60A5FA",
             animation: "syn-loader-node-a 1.6s ease-in-out infinite",
           }}>
          <circle cx={nodeAx} cy={nodeAy} r={s.dot} fill="#60A5FA" />
          <circle cx={nodeAx} cy={nodeAy} r={s.dot * 0.4} fill="#F8FAFC" opacity="0.95" />
        </g>

        {/* Neurone destro (smeraldo) */}
        <g style={{
             transformOrigin: `${nodeBx}px ${nodeBy}px`,
             transformBox: "fill-box",
             color: "#34D399",
             animation: "syn-loader-node-b 1.6s ease-in-out infinite",
           }}>
          <circle cx={nodeBx} cy={nodeBy} r={s.dot} fill="#34D399" />
          <circle cx={nodeBx} cy={nodeBy} r={s.dot * 0.4} fill="#F8FAFC" opacity="0.95" />
        </g>
      </svg>
      {label && !inline && (
        <div className={`${s.label} font-medium text-slate-400 tracking-wide`}
             style={{ animation: "syn-loader-label 1.6s ease-in-out infinite" }}>
          {label}
        </div>
      )}
      <span className="sr-only">Caricamento in corso</span>
    </div>
  );
}
